import React, { useMemo, useRef, useState } from 'react';
import { API_BASE_URL, apiUpload } from '../services/api';
import SearchableSelect from './SearchableSelect';

/**
 * Importar funcionários de uma planilha (.xlsx ou .csv).
 *
 * Feito para a planilha de respostas do Google Forms que as escolas usam para
 * recolher os dados dos professores — o cabeçalho é lido como vem. Duas
 * etapas, de propósito: a prévia mostra linha a linha o que vai acontecer
 * (quem entra, quem é atualizado, qual CPF está errado) e só o segundo
 * botão grava. Uma planilha de formulário sempre tem resposta repetida e
 * número estragado pelo Excel; gravar às cegas espalharia isso pelo RH.
 *
 * A escola vem da tela, não da planilha: na planilha da ECIM ela estava
 * escrita de 12 jeitos diferentes.
 */
function ImportarFuncionariosModal({ escolas, cargos, cityId, darkMode, onClose, onImported }) {
  const cargoProfessor = cargos.find((c) => c.slug === 'professor');
  const [idEscola, setIdEscola] = useState(escolas.length === 1 ? escolas[0].id : '');
  const [idCargo, setIdCargo] = useState(cargoProfessor?.id || '');
  const [arquivo, setArquivo] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const inputRef = useRef(null);

  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const forte = darkMode ? 'text-slate-200' : 'text-slate-700';

  // Mudou escola, cargo ou arquivo: a prévia anterior não vale mais.
  const mudar = (setter) => (valor) => {
    setter(valor);
    setPrevia(null);
    setResultado(null);
    setErro(null);
  };

  const enviar = async (dryRun) => {
    if (!arquivo || !idEscola) return;
    setEnviando(true);
    setErro(null);
    try {
      const formData = new FormData();
      formData.append('file', arquivo);
      formData.append('id_school', idEscola);
      if (idCargo) formData.append('id_role', idCargo);
      formData.append('dry_run', dryRun ? 'true' : 'false');
      const qs = cityId ? `?city=${cityId}` : '';
      const resp = await apiUpload(`/employees/import_spreadsheet/${qs}`, formData);
      if (dryRun) {
        setPrevia(resp);
      } else {
        setResultado(resp);
        setPrevia(null);
        onImported?.();
      }
    } catch (err) {
      setErro(err.response?.error || err.message || 'Não foi possível ler a planilha.');
    } finally {
      setEnviando(false);
    }
  };

  const [baixandoModelo, setBaixandoModelo] = useState(false);
  const baixarModelo = async () => {
    setBaixandoModelo(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/employees/import_template/`, { credentials: 'include' });
      if (!resp.ok) throw new Error();
      const url = URL.createObjectURL(await resp.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = 'modelo_importacao_funcionarios.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErro('Não foi possível baixar a planilha de exemplo.');
    } finally {
      setBaixandoModelo(false);
    }
  };

  const relatorio = resultado || previa;
  const aGravar = previa ? previa.criados + previa.atualizados : 0;

  const linhasOrdenadas = useMemo(() => {
    if (!relatorio) return [];
    // Erros primeiro: é o que pede ação de quem importa.
    const peso = { erro: 0, criar: 1, atualizar: 2 };
    return [...relatorio.linhas].sort(
      (a, b) => (peso[a.acao] - peso[b.acao]) || (a.linha - b.linha)
    );
  }, [relatorio]);

  const selo = {
    criar: { texto: resultado ? 'Criado' : 'Novo', cls: 'bg-[#13983D]/15 text-[#13983D]' },
    atualizar: { texto: resultado ? 'Atualizado' : 'Atualizar', cls: 'bg-[#0974BA]/15 text-[#0974BA]' },
    erro: { texto: 'Não entra', cls: 'bg-[#E83A3C]/15 text-[#E83A3C]' },
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-3xl rounded-2xl ${darkMode ? 'bg-slate-800' : 'bg-white'} shadow-2xl max-h-[90vh] overflow-y-auto`}>
        <div className={`p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'} flex items-center justify-between`}>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Importar funcionários
            </h2>
            <p className={`text-xs mt-1 ${muted}`}>
              Planilha .xlsx ou .csv — a de respostas do Google Forms serve como está.
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
            aria-label="Fechar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${forte}`}>Escola dos funcionários *</label>
              <SearchableSelect
                value={idEscola}
                onChange={mudar(setIdEscola)}
                options={escolas.map((e) => ({ value: e.id, label: e.school }))}
                placeholder="Selecione a escola..."
                darkMode={darkMode}
                className="w-full"
              />
              <p className={`text-[11px] mt-1 ${muted}`}>Todos da planilha ficam lotados nesta escola.</p>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${forte}`}>Cargo</label>
              <SearchableSelect
                value={idCargo}
                onChange={mudar(setIdCargo)}
                options={cargos.map((c) => ({ value: c.id, label: c.name }))}
                placeholder="Selecione o cargo..."
                darkMode={darkMode}
                className="w-full"
              />
              <p className={`text-[11px] mt-1 ${muted}`}>Só para quem ainda não tem cargo no cadastro.</p>
            </div>
          </div>

          <div
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${darkMode
              ? 'border-slate-600 hover:border-[#0974BA] bg-slate-900/30'
              : 'border-slate-300 hover:border-[#0974BA] bg-slate-50'}`}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && mudar(setArquivo)(e.target.files[0])}
            />
            <div className="text-3xl mb-2">📄</div>
            <p className={`text-sm font-medium ${forte}`}>
              {arquivo ? arquivo.name : 'Clique para escolher a planilha'}
            </p>
            <p className={`text-xs mt-1 ${muted}`}>
              Colunas lidas: Nome, CPF, Matrícula, RG, e-mail, Telefone, Data da Admissão,
              Tipo de Vínculo, Status, Turmas e Observações.
            </p>
          </div>

          <div className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg ${darkMode
            ? 'bg-[#13983D]/10 border border-[#13983D]/25'
            : 'bg-[#13983D]/5 border border-[#13983D]/25'}`}>
            <div>
              <p className="text-sm font-semibold text-[#13983D]">Não tem a planilha?</p>
              <p className={`text-xs mt-0.5 ${muted}`}>
                Baixe o modelo, preencha uma linha por funcionário e envie aqui.
              </p>
            </div>
            <button
              type="button"
              onClick={baixarModelo}
              disabled={baixandoModelo}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-[#13983D]/20 text-[#13983D] hover:bg-[#13983D]/30 transition-colors"
            >
              {baixandoModelo ? 'Baixando...' : '⬇ Baixar planilha de exemplo'}
            </button>
          </div>

          {erro && (
            <div className="p-3 rounded-lg bg-[#E83A3C]/10 border border-[#E83A3C]/30">
              <p className="text-sm text-[#E83A3C]">{erro}</p>
            </div>
          )}

          {relatorio && (
            <div className="space-y-3">
              {resultado && (
                <div className="p-3 rounded-lg bg-[#13983D]/10 border border-[#13983D]/30">
                  <p className="text-sm font-medium text-[#13983D]">
                    ✓ Importação concluída em {resultado.escola}.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { rotulo: 'Respostas lidas', valor: relatorio.linhas_lidas, cor: forte },
                  { rotulo: resultado ? 'Criados' : 'Novos', valor: relatorio.criados, cor: 'text-[#13983D]' },
                  { rotulo: resultado ? 'Atualizados' : 'Já cadastrados', valor: relatorio.atualizados, cor: 'text-[#0974BA]' },
                  { rotulo: 'Com erro', valor: relatorio.erros, cor: 'text-[#E83A3C]' },
                ].map((c) => (
                  <div key={c.rotulo} className={`rounded-lg p-3 text-center ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <div className={`text-xl font-bold ${c.cor}`}>{c.valor}</div>
                    <div className={`text-[11px] ${muted}`}>{c.rotulo}</div>
                  </div>
                ))}
              </div>
              {relatorio.respostas_repetidas > 0 && (
                <p className={`text-xs ${muted}`}>
                  {relatorio.respostas_repetidas} resposta(s) repetida(s) da mesma pessoa foram
                  desconsideradas — vale a mais recente de cada CPF.
                </p>
              )}
              <p className={`text-xs ${muted}`}>
                As turmas informadas vão para as observações de cada funcionário; a
                atribuição das turmas continua sendo feita no cadastro.
              </p>

              <div className={`rounded-lg border overflow-hidden ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className={darkMode ? 'bg-slate-700/60' : 'bg-slate-100'}>
                      <tr>
                        <th className={`px-3 py-2 text-left text-xs font-medium ${muted}`}>Linha</th>
                        <th className={`px-3 py-2 text-left text-xs font-medium ${muted}`}>Nome</th>
                        <th className={`px-3 py-2 text-left text-xs font-medium ${muted}`}>Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhasOrdenadas.map((l) => (
                        <tr key={l.linha} className={`border-t align-top ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                          <td className={`px-3 py-2 text-xs ${muted}`}>{l.linha}</td>
                          <td className={`px-3 py-2 ${forte}`}>
                            {l.nome || '—'}
                            {l.mensagem && <p className="text-xs text-[#E83A3C] mt-0.5">{l.mensagem}</p>}
                            {(l.avisos || []).map((a) => (
                              <p key={a} className={`text-xs mt-0.5 ${darkMode ? 'text-[#F9B047]' : 'text-[#B26B00]'}`}>{a}</p>
                            ))}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${selo[l.acao]?.cls}`}>
                              {selo[l.acao]?.texto}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {previa && previa.erros > 0 && (
                <p className={`text-xs ${muted}`}>
                  As linhas com erro ficam de fora. Corrija-as na planilha e importe de novo
                  depois — quem já entrou é só atualizado, sem duplicar.
                </p>
              )}
            </div>
          )}
        </div>

        <div className={`p-6 border-t flex flex-wrap justify-end gap-3 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium ${darkMode
              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            {resultado ? 'Fechar' : 'Cancelar'}
          </button>
          {!resultado && (previa ? (
            <button
              onClick={() => enviar(false)}
              disabled={enviando || aGravar === 0}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium text-white ${enviando || aGravar === 0
                ? 'bg-slate-500 cursor-not-allowed'
                : 'bg-[#13983D] hover:bg-[#13983D]/90'}`}
            >
              {enviando ? 'Importando...' : `Importar ${aGravar} funcionário(s)`}
            </button>
          ) : (
            <button
              onClick={() => enviar(true)}
              disabled={enviando || !arquivo || !idEscola}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium text-white ${enviando || !arquivo || !idEscola
                ? 'bg-slate-500 cursor-not-allowed'
                : 'bg-[#0974BA] hover:bg-[#0974BA]/90'}`}
            >
              {enviando ? 'Lendo planilha...' : 'Ver prévia'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ImportarFuncionariosModal;
